import { useLingui } from '@lingui/react/macro';
import { Card, CardContent, cn } from '@pantry/ui';
import { UNIT_DISPLAY_LABELS, formatQuantityDisplay } from '@pantry/api-client';
import {
  type Product,
  type Household,
  getDaysUntilExpiry,
  getExpiryStatus as getExpiryStatusShared,
  resolveExpiryThreshold,
} from '@pantry/shared';
import { getExpiryDisplayText } from '../../lib/expiry-display';
import { type LiveSelectedItem } from '@pantry/shared-react/hooks';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { ArchiveBoxIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Card for a selected item in the scan mode list.
 */
export function SelectedItemCard({
  selected,
  onRemove,
  household,
}: {
  selected: LiveSelectedItem;
  onRemove: () => void;
  household: Household | null;
}) {
  const { t, i18n } = useLingui();
  const resolveProductName = useResolveProductName();
  const itemName =
    selected.item.customName ||
    (selected.item.product ? resolveProductName(selected.item.product as Product) : null) ||
    t`Unnamed Item`;
  const itemBrand = selected.item.product?.brand;
  const itemImage = selected.item.product?.imageUrl;
  const quantity = selected.instance.quantity;
  const unit = selected.item.unit;

  // Calculate expiry status using resolved threshold
  const expiryDate = selected.instance.expiryDate;
  const daysUntilExpiry = expiryDate ? getDaysUntilExpiry(expiryDate) : null;
  const threshold = resolveExpiryThreshold(
    selected.item,
    selected.item.storageLocation ?? null,
    household
  );
  const expiryStatus = getExpiryStatusShared(daysUntilExpiry, threshold);

  return (
    <Card
      className={cn(
        'group hover:shadow-md transition-all duration-200 border-border/50 overflow-hidden',
        expiryStatus === 'expired' && 'border-expired/30',
        expiryStatus === 'expiring-today' && 'border-expiring-today/30',
        expiryStatus === 'expiring-soon' && 'border-expiring-soon/30'
      )}
    >
      <CardContent className="p-3 flex items-center gap-3">
        {/* Product image */}
        <div
          className={cn(
            'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl overflow-hidden',
            !itemImage && expiryStatus === 'expired' && 'bg-expired/10',
            !itemImage && expiryStatus === 'expiring-today' && 'bg-expiring-today/10',
            !itemImage && expiryStatus === 'expiring-soon' && 'bg-expiring-soon/10',
            !itemImage && (expiryStatus === 'fresh' || expiryStatus === 'none') && 'bg-muted'
          )}
        >
          {itemImage ? (
            <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
          ) : (
            <ArchiveBoxIcon
              className={cn(
                'h-5 w-5',
                expiryStatus === 'expired' && 'text-expired',
                expiryStatus === 'expiring-today' && 'text-expiring-today',
                expiryStatus === 'expiring-soon' && 'text-expiring-soon',
                (expiryStatus === 'fresh' || expiryStatus === 'none') && 'text-muted-foreground'
              )}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate text-foreground">{itemName}</h4>
          {itemBrand && <p className="text-xs text-muted-foreground truncate">{itemBrand}</p>}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-md">
              {formatQuantityDisplay(quantity, unit)} {UNIT_DISPLAY_LABELS[unit]}
            </span>
            {expiryDate && expiryStatus !== 'none' && (
              <span
                className={cn(
                  'text-xs px-2 py-0.5 rounded-md',
                  expiryStatus === 'expired' && 'bg-expired/10 text-expired',
                  expiryStatus === 'expiring-today' && 'bg-expiring-today/10 text-expiring-today',
                  expiryStatus === 'expiring-soon' && 'bg-expiring-soon/10 text-expiring-soon',
                  expiryStatus === 'fresh' && 'bg-muted/50 text-muted-foreground'
                )}
              >
                {getExpiryDisplayText(daysUntilExpiry, expiryDate, t, i18n.locale, threshold)}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onRemove}
          aria-label={t`Remove ${itemName}`}
          className="flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
}
